package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.connectivity.ConnectivityWatcher
import com.evoapps.evoshift.connectivity.ManualConnectivity
import com.evoapps.evoshift.domain.billing.PlanCheck
import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.model.mayAssign
import com.evoapps.evoshift.domain.spreadsheet.Spreadsheet
import com.evoapps.evoshift.domain.spreadsheet.SpreadsheetFormat
import com.evoapps.evoshift.domain.spreadsheet.SpreadsheetReader
import com.evoapps.evoshift.domain.staffimport.ImportEmployeeUseCase
import com.evoapps.evoshift.domain.staffimport.Inviter
import com.evoapps.evoshift.domain.staffimport.GetTeamForImportUseCase
import com.evoapps.evoshift.domain.staffimport.StaffColumnMapping
import com.evoapps.evoshift.domain.staffimport.StaffImportContext
import com.evoapps.evoshift.domain.staffimport.StaffImportField
import com.evoapps.evoshift.domain.staffimport.StaffImportMapping
import com.evoapps.evoshift.domain.staffimport.StaffImportRow
import com.evoapps.evoshift.domain.staffimport.StaffImportTemplate
import com.evoapps.evoshift.domain.staffimport.StaffImportValidator
import com.evoapps.evoshift.domain.staffimport.StaffRowCheck
import com.evoapps.evoshift.domain.staffimport.TeamForImport
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.CheckSeatsUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.spreadsheet.PickedFile
import com.evoapps.evoshift.ui.reports.ExportFile
import com.evoapps.evoshift.ui.reports.FileSaver
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.sales_import_file_unreadable
import evoshift.composeapp.generated.resources.staff_import_name_column_needed
import evoshift.composeapp.generated.resources.staff_import_needs_connection
import evoshift.composeapp.generated.resources.staff_import_template_saved
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext

/** The import's steps: where the cells come from, what the columns hold, the people, the outcome. */
enum class StaffImportStep { SOURCE, MAPPING, PREVIEW, DONE }

/** A column of the file for the mapping step: its letter's index, its header and a few values. */
data class StaffImportColumn(val index: Int, val header: String, val samples: List<String>)

/** A person's row in the preview. */
data class StaffImportRowState(
    val check: StaffRowCheck,
    /** Left out by the person ("Skip"). */
    val skipped: Boolean = false,
    /** Added to the team (by an earlier "Import" of this screen). */
    val saved: Boolean = false,
    /** Adding them failed: why (the row can be imported again). */
    val failure: UiText? = null
) {
    val number: Int get() = check.row.number
    /** Imported by the next "Import": valid, not skipped, not added yet. */
    val isReady: Boolean get() = check.isValid && !skipped && !saved
    /** Needs the person: fix or skip. */
    val needsFix: Boolean get() = !check.isValid && !skipped && !saved
}

/** What one "Import" did. */
data class StaffImportSummary(val added: Int, val invited: Int, val inviteFailed: Int, val failed: Int)

data class StaffImportUiState(
    /** Only until it is known who looks and what the workplace is. */
    val isLoading: Boolean = true,
    val capabilities: Set<Capability> = emptySet(),
    /** The workplace's currency, for the rates. */
    val currency: String = "PLN",
    // --- The source ---
    val pasted: String = "",
    val isReading: Boolean = false,
    val fileName: String? = null,
    val format: SpreadsheetFormat? = null,
    val sheetNames: List<String> = emptyList(),
    val sheetIndex: Int = 0,
    // --- The mapping ---
    val step: StaffImportStep = StaffImportStep.SOURCE,
    val columns: List<StaffImportColumn> = emptyList(),
    val mapping: StaffColumnMapping = StaffColumnMapping(null, emptyList()),
    // --- The preview ---
    val rows: List<StaffImportRowState> = emptyList(),
    /** The row being fixed (its number), with its values as edited. */
    val editingRow: Int? = null,
    /** How many more people fit the plan's seats (null: no limit, or unknown); SH-220: information only. */
    val seatsLeft: Int? = null,
    /** The plan ended (read-only): the screen explains and, for the owner, offers the plans. */
    val planLimit: PlanCheck.LimitReached? = null,
    // --- Importing ---
    val isImporting: Boolean = false,
    val importedSoFar: Int = 0,
    val importTotal: Int = 0,
    val summary: StaffImportSummary? = null,
    val message: UiText? = null
) {
    /** Adding people: owners and managers, not once the plan ended (read-only, SH-65). */
    val canImport: Boolean get() = capabilities.shows(Gate.TEAM_EDITING)

    /** Pay, contract, student status and phone: only for those who may write them (MANAGE_PAY). */
    val includesPrivate: Boolean get() = capabilities.shows(Gate.PAY_AND_PRIVATE_DATA)

    /** The plan choice for the owner (others ask the owner). */
    val canChoosePlan: Boolean get() = capabilities.shows(Gate.PLAN)

    /** The fields the mapping offers. */
    val offeredFields: List<StaffImportField> get() = StaffImportField.offered(includesPrivate)

    val readyRows: List<StaffImportRowState> get() = rows.filter { it.isReady }
    val rowsToFix: Int get() = rows.count { it.needsFix }
    val skippedRows: Int get() = rows.count { it.skipped && !it.saved }

    /**
     * The ready rows need more seats than the plan has left. SH-220: information only, the import
     * still saves everyone (growing never blocks) and the owner is offered the next tier.
     */
    val exceedsSeats: Boolean get() = seatsLeft?.let { readyRows.size > it } == true

    val editing: StaffImportRowState? get() = editingRow?.let { number -> rows.firstOrNull { it.number == number } }
}

/**
 * SH-214: "Import from a spreadsheet" (Team), owners and managers. Cells pasted from Excel or
 * Google Sheets, or a .csv/.xlsx file picked or dropped, are read on the device
 * ([SpreadsheetReader]); the person maps the columns to our fields (suggested from the headers in
 * Polish, English or Ukrainian), sees every person checked ([StaffImportValidator]: missing name,
 * bad e-mail, a rate that is not a number, an e-mail twice in the file or already in the team...),
 * fixes or skips the rows with problems, and imports: one person at a time through the manual add
 * path ([ImportEmployeeUseCase]); only an ended plan stops it ([CheckSeatsUseCase]): past the
 * plan's seats everyone is saved and the owner is offered the next tier (SH-220). Nothing is written
 * before "Import".
 */
class StaffImportViewModel(
    private val workplaceId: String,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getWorkplace: GetWorkplaceUseCase,
    private val getTeam: GetTeamForImportUseCase,
    private val importEmployee: ImportEmployeeUseCase,
    private val fileSaver: FileSaver,
    private val checkSeats: CheckSeatsUseCase? = null,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val connectivity: ConnectivityWatcher = ManualConnectivity(),
    /** The language of the template handed out ("pl", "en", "uk"). */
    private val language: () -> String = { "en" },
    /** Where files are read (off the main thread; tests read them in place). */
    private val parsing: CoroutineDispatcher = Dispatchers.Default
) : ViewModel() {

    private val _uiState = MutableStateFlow(StaffImportUiState())
    val uiState: StateFlow<StaffImportUiState> = _uiState.asStateFlow()

    private var spreadsheet: Spreadsheet? = null
    /** The person's fixes, by row number: the cells as they typed them. */
    private val edits = mutableMapOf<Int, Map<StaffImportField, String>>()
    private var context = StaffImportContext()
    private var workplaceName = ""
    private var currency = "PLN"
    private var inviter: Inviter? = null
    private var team = TeamForImport(emptyList(), emptySet())

    init {
        viewModelScope.launch {
            val me = getCurrentUser().first()
            val myPermission = me?.engagements?.get(workplaceId)?.takeIf { it.isActive }?.permission
            val capabilities = me?.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId)).orEmpty()
            inviter = me?.let { Inviter(it.uid, it.displayName) }
            val workplace = getWorkplace(workplaceId).getOrNull()
            workplaceName = workplace?.name.orEmpty()
            currency = workplace?.settings?.currency?.takeIf { it.isNotBlank() } ?: "PLN"
            val settings = workplace?.settings
            context = StaffImportContext(
                allowedPermissions = UserPermission.entries.filterTo(HashSet()) { myPermission?.mayAssign(it) == true && it != UserPermission.OWNER },
                workplaceRoles = settings?.availableRoles.orEmpty(),
                leadRoles = settings?.leadRoles.orEmpty(),
                leadSkill = settings?.leadSkills?.firstOrNull { it.isNotBlank() },
                phoneCountryCode = if (settings?.market == Market.POLAND || settings == null) "48" else null
            )
            _uiState.update { it.copy(isLoading = false, capabilities = capabilities, currency = currency) }
            if (capabilities.shows(Gate.TEAM_EDITING)) {
                // The team for the duplicate checks; e-mails only for those who may read them.
                team = getTeam(workplaceId, withEmails = capabilities.shows(Gate.PAY_AND_PRIVATE_DATA)).getOrDefault(team)
                context = context.copy(teamNames = team.names, teamEmails = team.emails)
                if (_uiState.value.step == StaffImportStep.PREVIEW) revalidate()
            }
        }
    }

    // --- The source --------------------------------------------------------------------------

    fun onPastedChanged(text: String) = _uiState.update { it.copy(pasted = text) }

    /** Reads the pasted cells. */
    fun readPasted() {
        val text = _uiState.value.pasted
        if (!_uiState.value.canImport || text.isBlank()) return
        runCatching { SpreadsheetReader.readPasted(text) }.fold(
            onSuccess = { opened(it, fileName = null) },
            onFailure = { e -> _uiState.update { it.copy(message = e.toUiText()) } }
        )
    }

    /** A file picked or dropped (null: it could not be read). */
    fun onFile(file: PickedFile?) {
        if (!_uiState.value.canImport || _uiState.value.isImporting) return
        if (file == null) {
            _uiState.update { it.copy(message = UiText.Resource(Res.string.sales_import_file_unreadable)) }
            return
        }
        _uiState.update { it.copy(isReading = true, fileName = file.name) }
        viewModelScope.launch {
            runCatching { withContext(parsing) { SpreadsheetReader.read(file.bytes) } }.fold(
                onSuccess = { opened(it, file.name) },
                onFailure = { e -> _uiState.update { it.copy(isReading = false, fileName = null, message = e.toUiText()) } }
            )
        }
    }

    private fun opened(file: Spreadsheet, fileName: String?) {
        spreadsheet = file
        edits.clear()
        context = context.copy(numbersAsStored = file.format == SpreadsheetFormat.XLSX)
        val sheet = file.firstFilledSheet
        _uiState.update {
            it.copy(
                isReading = false,
                fileName = fileName,
                format = file.format,
                sheetNames = file.sheets.map { s -> s.name },
                sheetIndex = sheet,
                rows = emptyList(),
                summary = null,
                planLimit = null
            )
        }
        suggestMapping(sheet)
    }

    /** Another sheet of the workbook: its columns are suggested afresh. */
    fun selectSheet(index: Int) {
        val file = spreadsheet ?: return
        if (index !in file.sheets.indices || _uiState.value.isImporting) return
        edits.clear()
        _uiState.update { it.copy(sheetIndex = index, rows = emptyList()) }
        suggestMapping(index)
    }

    private fun suggestMapping(sheet: Int) {
        val rows = sheetRows(sheet)
        val mapping = StaffImportMapping.suggest(rows, _uiState.value.offeredFields)
        _uiState.update { it.copy(step = StaffImportStep.MAPPING, mapping = mapping, columns = columnsOf(rows, mapping.headerRow)) }
    }

    private fun sheetRows(sheet: Int = _uiState.value.sheetIndex): List<List<String>> = spreadsheet?.sheets?.getOrNull(sheet)?.rows.orEmpty()

    private fun columnsOf(rows: List<List<String>>, headerRow: Int?): List<StaffImportColumn> {
        val width = rows.maxOfOrNull { it.size } ?: 0
        val first = rows.indexOfFirst { row -> row.any { it.isNotBlank() } }.coerceAtLeast(0)
        val dataFrom = (headerRow ?: (first - 1)) + 1
        return (0 until width).map { column ->
            StaffImportColumn(
                index = column,
                header = headerRow?.let { rows[it].getOrNull(column)?.trim() }.orEmpty(),
                samples = rows.drop(dataFrom).mapNotNull { it.getOrNull(column)?.trim()?.takeIf(String::isNotEmpty) }.take(3)
            )
        }.filter { it.header.isNotEmpty() || it.samples.isNotEmpty() }
    }

    // --- The mapping -------------------------------------------------------------------------

    /** Whether the first row holds the column names (it then is not a person). */
    fun setHasHeader(hasHeader: Boolean) {
        val rows = sheetRows()
        val first = rows.indexOfFirst { row -> row.any { it.isNotBlank() } }
        if (first < 0) return
        val headerRow = if (hasHeader) first else null
        val mapping = if (hasHeader) {
            StaffColumnMapping(first, StaffImportMapping.headerFields(rows[first], rows.maxOf { it.size }, _uiState.value.offeredFields))
                .takeIf { m -> m.fields.any { it != null } } ?: _uiState.value.mapping.copy(headerRow = first)
        } else _uiState.value.mapping.copy(headerRow = null)
        _uiState.update { it.copy(mapping = mapping.copy(headerRow = headerRow), columns = columnsOf(rows, headerRow)) }
    }

    /** [column] holds [field] (null: not imported). */
    fun mapColumn(column: Int, field: StaffImportField?) {
        if (field != null && field !in _uiState.value.offeredFields) return
        _uiState.update { it.copy(mapping = it.mapping.with(column, field)) }
    }

    /** To the preview: needs the column with the names. */
    fun showPreview() {
        val state = _uiState.value
        if (!state.mapping.hasName) {
            _uiState.update { it.copy(message = UiText.Resource(Res.string.staff_import_name_column_needed)) }
            return
        }
        edits.clear()
        val rows = StaffImportMapping.rows(sheetRows(), state.mapping)
        _uiState.update { it.copy(step = StaffImportStep.PREVIEW, rows = check(rows, emptySet(), emptyMap()), summary = null) }
        refreshSeats()
    }

    fun backToMapping() {
        if (_uiState.value.isImporting) return
        _uiState.update { it.copy(step = StaffImportStep.MAPPING, editingRow = null, planLimit = null) }
    }

    /** Back to the start (another file or paste). */
    fun startOver() {
        if (_uiState.value.isImporting) return
        spreadsheet = null
        edits.clear()
        _uiState.update {
            it.copy(
                step = StaffImportStep.SOURCE, fileName = null, format = null, sheetNames = emptyList(), sheetIndex = 0,
                columns = emptyList(), mapping = StaffColumnMapping(null, emptyList()), rows = emptyList(), editingRow = null,
                summary = null, planLimit = null
            )
        }
    }

    // --- The preview -------------------------------------------------------------------------

    private fun check(
        rows: List<StaffImportRow>,
        skipped: Set<Int>,
        previous: Map<Int, StaffImportRowState>
    ): List<StaffImportRowState> {
        val fixed = rows.map { row -> edits[row.number]?.let { row.copy(values = row.values + it) } ?: row }
        // People this screen already added are part of the team for the duplicate checks.
        val saved = previous.values.filter { it.saved }
        val checks = StaffImportValidator.check(
            fixed,
            context.copy(
                teamEmails = context.teamEmails + saved.mapNotNull { it.check.candidate.email },
                teamNames = context.teamNames + saved.map { it.check.candidate.displayName }
            ),
            skipped = skipped + saved.map { it.number }
        )
        return checks.map { check ->
            val before = previous[check.row.number]
            if (before?.saved == true) before
            else StaffImportRowState(check, skipped = check.row.number in skipped, failure = before?.failure)
        }
    }

    private fun revalidate() {
        val state = _uiState.value
        val previous = state.rows.associateBy { it.number }
        val rows = StaffImportMapping.rows(sheetRows(), state.mapping)
        _uiState.update { it.copy(rows = check(rows, state.rows.filter { r -> r.skipped }.mapTo(HashSet()) { r -> r.number }, previous)) }
    }

    /** "Skip" / "Include again". */
    fun toggleSkip(rowNumber: Int) {
        val state = _uiState.value
        if (state.isImporting) return
        val target = state.rows.firstOrNull { it.number == rowNumber } ?: return
        if (target.saved) return
        _uiState.update { s -> s.copy(rows = s.rows.map { if (it.number == rowNumber) it.copy(skipped = !it.skipped) else it }) }
        revalidate()
    }

    /** "Skip all rows with problems": they stay listed and can be brought back. */
    fun skipAllWithProblems() {
        if (_uiState.value.isImporting) return
        _uiState.update { s -> s.copy(rows = s.rows.map { if (it.needsFix) it.copy(skipped = true) else it }) }
        revalidate()
    }

    /** "Fix": the row's values open in a dialog. */
    fun startEdit(rowNumber: Int) {
        val row = _uiState.value.rows.firstOrNull { it.number == rowNumber } ?: return
        if (row.saved || _uiState.value.isImporting) return
        _uiState.update { it.copy(editingRow = rowNumber) }
    }

    fun cancelEdit() = _uiState.update { it.copy(editingRow = null) }

    /** The fixed values of the row being edited; checked again with the others. */
    fun saveEdit(values: Map<StaffImportField, String>) {
        val number = _uiState.value.editingRow ?: return
        edits[number] = values.mapValues { it.value.trim() }
        _uiState.update { s -> s.copy(editingRow = null, rows = s.rows.map { if (it.number == number) it.copy(skipped = false) else it }) }
        revalidate()
    }

    private fun refreshSeats() {
        viewModelScope.launch {
            val left = checkSeats?.seatsLeft(workplaceId)
            _uiState.update { it.copy(seatsLeft = left) }
        }
    }

    // --- Template and import -----------------------------------------------------------------

    fun downloadTemplate() {
        val lang = language()
        fileSaver.save(ExportFile(StaffImportTemplate.fileName(lang), "text/csv", StaffImportTemplate.csv(lang)))
        _uiState.update { it.copy(message = UiText.Resource(Res.string.staff_import_template_saved)) }
    }

    /** "Import N people": the ready rows, one by one; rows with problems or skipped stay. */
    fun import() {
        val state = _uiState.value
        val ready = state.readyRows
        if (!state.canImport || state.isImporting || ready.isEmpty() || state.rowsToFix > 0) return
        if (!connectivity.isOnline.value) {
            // Invitations are created by the server: an import waits for a connection.
            _uiState.update { it.copy(message = UiText.Resource(Res.string.staff_import_needs_connection)) }
            return
        }
        _uiState.update { it.copy(isImporting = true, importedSoFar = 0, importTotal = ready.size, planLimit = null) }
        viewModelScope.launch {
            // SH-220: only an ended plan refuses; past the seats (PlanCheck.OverPlan) everyone is saved.
            val seats = checkSeats?.invoke(workplaceId, adding = ready.size)
            if (seats is PlanCheck.LimitReached) {
                val left = checkSeats.seatsLeft(workplaceId)
                _uiState.update { it.copy(isImporting = false, planLimit = seats, seatsLeft = left) }
                return@launch
            }
            var added = 0
            var invited = 0
            var inviteFailed = 0
            var failed = 0
            for (row in ready) {
                val result = importEmployee(
                    workplaceId = workplaceId,
                    workplaceName = workplaceName,
                    candidate = row.check.candidate,
                    includeSensitive = state.includesPrivate,
                    inviter = inviter,
                    currency = currency
                )
                result.fold(
                    onSuccess = { imported ->
                        added++
                        if (imported.inviteCode != null) invited++
                        if (imported.inviteFailed) inviteFailed++
                    },
                    onFailure = { failed++ }
                )
                _uiState.update { s ->
                    s.copy(
                        importedSoFar = s.importedSoFar + 1,
                        rows = s.rows.map {
                            if (it.number != row.number) it
                            else result.fold(
                                onSuccess = { _ -> it.copy(saved = true, failure = null) },
                                onFailure = { e -> it.copy(failure = e.toUiText()) }
                            )
                        }
                    )
                }
            }
            _uiState.update {
                it.copy(
                    isImporting = false,
                    step = StaffImportStep.DONE,
                    summary = StaffImportSummary(added, invited, inviteFailed, failed)
                )
            }
        }
    }

    /** Back from the summary to the rows that failed or were skipped. */
    fun reviewRemaining() {
        if (_uiState.value.isImporting) return
        _uiState.update { it.copy(step = StaffImportStep.PREVIEW, summary = null) }
        revalidate()
        refreshSeats()
    }

    fun planLimitShown() = _uiState.update { it.copy(planLimit = null) }

    fun messageShown() = _uiState.update { it.copy(message = null) }

}
